/**
 * @format
 * @module lib/identity/akamai
 * @description The `ak_user` cookie is set by the Akamai CDN for each user, on every page view of the Globe.
 * It's basically read-only, and can't be overwritten via browser devtools, for debugging.
 */
import cookies from "js-cookie";
import { getProp } from "utils/utils";
import logger from "utils/logger";

export const AK_COOKIE = "ak_user";

const debugTag = "[AKAMAI-COOKIE]";

/**
 * Get the Akamai cookie value and return as Object
 * @returns {Object}
 * @example
 * {
 *   latitude: "43.67",
 *   longitude: "-79.42",
 *   timezone: "EST",
 *   network: "rogers",
 *   country_code: "CA",
 *   region_code: "ON",
 *   city: "TORONTO",
 *   postal_code: "M3H+M3M",
 *   ip: "99.230.211.234",
 *   is_mobile: "false",
 *   is_wireless: "false",
 *   is_tablet: "false",
 *   device_os: "Mac OS X"
 * }
 */
export function getAkamaiCookie() {
  const cookie = cookies.get(AK_COOKIE);
  if (!cookie) {
    return {};
  }

  const cookieParse = c => {
    const value = JSON.parse(c);
    logger.log(`${debugTag} value:`, value);
    return value;
  };

  try {
    return cookieParse(cookie);
  } catch (error) {
    let e = error;

    window.analyticsLayer.push({
      event: "simpleEvent",
      simpleEvent: {
        category: "ak_user cookie",
        action: "malformed cookie value",
        label: "error and cookie",
        value: `${e.message} - ${cookie}`
      }
    });

    // Certain dev's were seeing the `ip` property missing a leading double-quote.
    // Here are are looking for that and inserting the missing quote, before parsing the JSON.
    // RegExp should match "ip":127.0.0.1" and not match: "ip":"127.0.0.1"
    if (/(\"ip\"\:\d)/.test(cookie)) {
      try {
        // eslint-disable-next-line quotes
        return cookieParse(cookie.replace(`"ip":`, `"ip":"`));
      } catch (error2) {
        e = error2;
      }
    }

    logger.error(`${debugTag} error:`, e.message, cookie);
    return {};
  }
}

/**
 * Get the Akamai cookie's country and region codes. e.g. ["CA", "ON"]
 * @param {Object} cookie - full cookie object
 * @returns {[String, String]} of [country, region]
 */
export function selectAkamaiLocation(cookie) {
  return [getProp(cookie, "country_code", ""), getProp(cookie, "region_code", "")];
}
